import { DayOfWeek, DAYS_OF_WEEK_FROM_MONDAY, DAYS_OF_WEEK_FROM_SUNDAY } from '../constants/daysOfWeek';

/**
 * Get the index of a day in the week (0-6)
 * @param day - The day of the week
 * @param startFromSunday - Whether to start from Sunday (true) or Monday (false, default)
 * @returns The index of the day (0-6) or -1 if invalid
 */
export const getDayIndex = (day: DayOfWeek, startFromSunday = false): number => {
    const daysArray = startFromSunday ? DAYS_OF_WEEK_FROM_SUNDAY : DAYS_OF_WEEK_FROM_MONDAY;
    return daysArray.indexOf(day);
};

/**
 * Get the day of week from an index (0-6)
 * @param index - The index (0-6)
 * @param startFromSunday - Whether to start from Sunday (true) or Monday (false, default)
 * @returns The day of the week or undefined if invalid index
 */
export const getDayFromIndex = (index: number, startFromSunday = false): DayOfWeek | undefined => {
    const daysArray = startFromSunday ? DAYS_OF_WEEK_FROM_SUNDAY : DAYS_OF_WEEK_FROM_MONDAY;
    return daysArray[index];
};

/**
 * Check if a string is a valid day of week
 * @param value - The value to check
 * @returns True if the value is a valid day of week
 */
export const isValidDayOfWeek = (value: string): value is DayOfWeek => {
    return DAYS_OF_WEEK_FROM_SUNDAY.includes(value as DayOfWeek);
};

/**
 * Sort an array of days in week order
 * @param days - Array of days to sort
 * @param startFromSunday - Whether to start from Sunday (true) or Monday (false, default)
 * @returns Sorted array of days
 */
export const sortDaysByWeekOrder = (days: DayOfWeek[], startFromSunday = false): DayOfWeek[] => {
    const daysArray = startFromSunday ? DAYS_OF_WEEK_FROM_SUNDAY : DAYS_OF_WEEK_FROM_MONDAY;
    return days.sort((a, b) => daysArray.indexOf(a) - daysArray.indexOf(b));
};

/**
 * Get the previous day in the week
 * @param day - The current day
 * @param startFromSunday - Whether to start from Sunday (true) or Monday (false, default)
 * @returns The previous day
 */
export const getPreviousDay = (day: DayOfWeek, startFromSunday = false): DayOfWeek => {
    const daysArray = startFromSunday ? DAYS_OF_WEEK_FROM_SUNDAY : DAYS_OF_WEEK_FROM_MONDAY;
    const index = daysArray.indexOf(day);
    const previousIndex = index === 0 ? daysArray.length - 1 : index - 1;
    return daysArray[previousIndex];
};

/**
 * Get the next day in the week
 * @param day - The current day
 * @param startFromSunday - Whether to start from Sunday (true) or Monday (false, default)
 * @returns The next day
 */
export const getNextDay = (day: DayOfWeek, startFromSunday = false): DayOfWeek => {
    const daysArray = startFromSunday ? DAYS_OF_WEEK_FROM_SUNDAY : DAYS_OF_WEEK_FROM_MONDAY;
    const index = daysArray.indexOf(day);
    const nextIndex = (index + 1) % daysArray.length;
    return daysArray[nextIndex];
};

/**
 * Check if a day is a weekend day
 * @param day - The day to check
 * @returns True if the day is Saturday or Sunday
 */
export const isWeekendDay = (day: DayOfWeek): boolean => {
    return day === 'saturday' || day === 'sunday';
};

/**
 * Check if a day is a weekday
 * @param day - The day to check
 * @returns True if the day is Monday through Friday
 */
export const isWeekday = (day: DayOfWeek): boolean => {
    return !isWeekendDay(day);
};
